import { createContext, useContext, useEffect, useState } from "react";

import { api, getErrorMessage } from "@/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!window.localStorage.getItem("atelier-token")) {
      setChecking(false);
      return;
    }
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => window.localStorage.removeItem("atelier-token"))
      .finally(() => setChecking(false));
  }, []);

  const login = async (credentials) => {
    try {
      const { data } = await api.post("/auth/login", credentials);
      window.localStorage.setItem("atelier-token", data.access_token);
      if (data.refresh_token) window.localStorage.setItem("atelier-refresh", data.refresh_token);
      setUser(data.user);
      return data.user;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    window.localStorage.removeItem("atelier-token");
    window.localStorage.removeItem("atelier-refresh");
    setUser(null);
  };
  const changePassword = async (payload) => {
    try {
      await api.post("/auth/change-password", payload);
      setUser((current) => ({ ...current, force_password_change: false }));
    } catch (error) { throw new Error(getErrorMessage(error)); }
  };
  const updateProfile = async (payload) => {
    try {
      const { data } = await api.patch("/auth/me", payload);
      setUser((current) => ({ ...current, ...data }));
      return data;
    } catch (error) { throw new Error(getErrorMessage(error)); }
  };
  const uploadProfilePhoto = async (file) => {
    try {
      const { compressImage } = await import("@/imageUtils");
      const formData = new FormData();
      formData.append("file", await compressImage(file));
      const { data } = await api.post("/auth/me/photo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setUser((current) => ({ ...current, ...data }));
      return data;
    } catch (error) { throw new Error(getErrorMessage(error)); }
  };
  const removeProfilePhoto = async () => {
    try {
      const { data } = await api.delete("/auth/me/photo");
      setUser((current) => ({ ...current, ...data, profile_photo: null }));
      return data;
    } catch (error) { throw new Error(getErrorMessage(error)); }
  };
  const selectActiveRole = async (role) => {
    try {
      const { data } = await api.post("/auth/active-role", { role });
      window.localStorage.setItem("atelier-token", data.access_token);
      if (data.refresh_token) window.localStorage.setItem("atelier-refresh", data.refresh_token);
      setUser(data.user);
      return data.user;
    } catch (error) { throw new Error(getErrorMessage(error)); }
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, logout, changePassword, updateProfile, uploadProfilePhoto, removeProfilePhoto, selectActiveRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}